import * as React from "react";
import { cn } from "@/lib/utils";
import { Combobox } from "@/components/ui/combobox";

export const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  ({ className, ...props }, ref) => (
    <input
      ref={ref}
      className={cn(
        "h-9 w-full rounded-md border border-input bg-secondary px-3 text-base md:text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring placeholder:text-muted-foreground",
        className
      )}
      {...props}
    />
  )
);
Input.displayName = "Input";

export interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  placeholder?: string;
}

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, "aria-label": ariaLabel, children, value, onChange, disabled, placeholder, ...props }, ref) => {
    const { options, hasEmpty, emptyLabel } = React.useMemo(() => {
      const opts: { value: string; label: string }[] = [];
      let empty = false;
      let emptyTxt = "—";

      const extract = (nodes: React.ReactNode) => {
        React.Children.forEach(nodes, (node) => {
          if (!React.isValidElement(node)) return;
          const child = node as React.ReactElement<{ value?: any; children?: React.ReactNode }>;
          if (child.type === "option") {
            const val = child.props.value !== undefined ? String(child.props.value) : "";
            const text =
              typeof child.props.children === "string"
                ? child.props.children
                : Array.isArray(child.props.children)
                ? child.props.children.filter(Boolean).join(" ")
                : String(child.props.children ?? val);
            if (val === "") {
              empty = true;
              emptyTxt = text || "—";
            } else {
              opts.push({ value: val, label: text || val });
            }
          } else if (child.props?.children) {
            extract(child.props.children);
          }
        });
      };

      extract(children);
      return { options: opts, hasEmpty: empty, emptyLabel: emptyTxt };
    }, [children]);

    const handleChange = React.useCallback(
      (newVal: string) => {
        if (!onChange) return;
        const event = {
          target: { value: newVal, name: props.name },
          currentTarget: { value: newVal, name: props.name },
          preventDefault: () => {},
          stopPropagation: () => {},
        } as unknown as React.ChangeEvent<HTMLSelectElement>;
        onChange(event);
      },
      [onChange, props.name]
    );

    return (
      <div className={cn("relative", className?.includes("w-full") ? "w-full" : "inline-block")}>
        <select
          ref={ref}
          aria-hidden="true"
          tabIndex={-1}
          value={value ?? ""}
          onChange={onChange}
          disabled={disabled}
          className="sr-only pointer-events-none"
          {...props}
        >
          {children}
        </select>
        <Combobox
          value={value != null ? String(value) : ""}
          onChange={handleChange}
          options={options}
          allowEmpty={hasEmpty}
          emptyLabel={emptyLabel}
          ariaLabel={ariaLabel}
          placeholder={placeholder}
          className={className}
        />
      </div>
    );
  }
);
Select.displayName = "Select";
